/**
 * Presentation helpers for CRM status-like fields.
 *
 * Callers typically hold a `Tables<'crm_contacts'>` row where `status` /
 * `action_state` are plain `string` (the DB CHECK constraint and the TS
 * union have already drifted once), so raw values must be narrowed against
 * the canonical label maps in `@/lib/crm/types` before they are trusted.
 * Unknown values degrade to a visible-but-safe fallback instead of
 * throwing or silently mislabeling the row.
 */
import {
  ACTION_STATE_COLORS,
  ACTION_STATE_LABELS,
  STATUS_COLORS,
  STATUS_LABELS,
  type ActionState,
  type ContactStatus,
} from '@/lib/crm/types';

export interface StatusPresentation {
  label: string;
  className: string;
  isKnown: boolean;
}

const NEUTRAL_CLASS_NAME = 'bg-surface text-muted-foreground border-border';

const CONTACT_STATUS_KEYS: readonly string[] = Object.keys(STATUS_LABELS);
const ACTION_STATE_KEYS: readonly string[] = Object.keys(ACTION_STATE_LABELS);

const isContactStatus = (value: string): value is ContactStatus =>
  CONTACT_STATUS_KEYS.includes(value);

const isActionState = (value: string): value is ActionState =>
  ACTION_STATE_KEYS.includes(value);

const humanize = (value: string): string =>
  value
    .split(/[_\s-]+/)
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

export const toContactStatus = (value: string | null | undefined): ContactStatus | null => {
  if (!value || !value.trim()) return null;
  return isContactStatus(value) ? value : null;
};

export const toActionState = (value: string | null | undefined): ActionState | null => {
  if (!value || !value.trim()) return null;
  return isActionState(value) ? value : null;
};

export const resolveStatusPresentation = (
  value: string | null | undefined,
): StatusPresentation | null => {
  if (!value || !value.trim()) return null;

  const known = toContactStatus(value);
  if (known) {
    return { label: STATUS_LABELS[known], className: STATUS_COLORS[known], isKnown: true };
  }

  return { label: humanize(value), className: NEUTRAL_CLASS_NAME, isKnown: false };
};

export const resolveActionStatePresentation = (
  value: string | null | undefined,
): StatusPresentation | null => {
  if (!value || !value.trim()) return null;

  const known = toActionState(value);
  if (known) {
    return {
      label: ACTION_STATE_LABELS[known],
      className: ACTION_STATE_COLORS[known],
      isKnown: true,
    };
  }

  return { label: humanize(value), className: NEUTRAL_CLASS_NAME, isKnown: false };
};
